import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const sonnerToast = toast;
const noop = () => null;
// Background auto-refresh must not pop toasts every 30 s.
const QUIET_TOAST = { loading: noop, success: noop, warning: noop, error: noop, info: noop };
import { todayLocalIso } from "../lib/dateHelpers";
import { computeLeg2SLLWP } from "../lib/leg2Fill";
import { useAutoRefresh } from "../hooks/useAutoRefresh";

const REFRESH_MS = 30000;

// Merge one entry list (DD or ML) into leftRows. Field is "DD" or "ML".
const applyEntries = (rows, entries, field) => {
  const next = rows.slice();
  let filled = 0;
  for (const ent of entries) {
    const s = Number(ent.S);
    if (!Number.isInteger(s) || s < 1) continue;
    while (next.length < s && next.length < 20) {
      next.push({ RANK: "", S: next.length + 1, DD: "", ML: "" });
    }
    const idx = next.findIndex((r) => Number(r.S) === s);
    if (idx === -1) continue;
    const v = String(ent.value ?? "").trim();
    if (!v) continue;
    next[idx] = { ...next[idx], [field]: v };
    if (field === "DD") {
      next[idx].DDFlag = ent.unverified
        ? "unverified"
        : ent.cybertote_value
        ? "mismatch"
        : null;
      next[idx].DDCt = ent.cybertote_value || "";
    }
    filled += 1;
  }
  return { next, filled };
};

// Blank DD on posts Cybertote left without a will-pay so stale values don't linger.
const clearDD = (rows, posts = []) =>
  rows.map((r) =>
    posts.includes(Number(r.S)) ? { ...r, DD: "", DDFlag: null, DDCt: "" } : r
  );

// Extract the top-N DDPO posts (S values + DDPO) from handicapped leftRows.
const topDDPORows = (leftRows, n = 3) =>
  leftRows
    .filter((r) => Number(r.RANK) >= 1)
    .sort((a, b) => Number(a.RANK) - Number(b.RANK))
    .slice(0, n)
    .map((r) => {
      const dd = Number(String(r.DD).replace(/[,$]/g, ""));
      // ML like "5/2" or "3"
      const s = String(r.ML).trim();
      let ml = Number(s);
      if (!Number.isFinite(ml) && s.includes("/")) {
        const [a, b] = s.split("/").map((p) => Number(p.trim()));
        if (Number.isFinite(a) && Number.isFinite(b) && b !== 0) ml = a / b;
      }
      const DDPO =
        Number.isFinite(dd) && Number.isFinite(ml) && ml > 0 ? dd / ml : null;
      return { S: Number(r.S), DDPO };
    });

export const TrackLoader = ({
  apiBase,
  setLeftRows,
  setRightRows,
  leftRows,
  handicapped,
  onFilled,
  onLeg2Filled,
  onHandicap,
  onTrackContext,
  onLoaded,
  pendingLoad,
  auto,
}) => {
  const [tracks, setTracks] = useState([]);
  const [track, setTrack] = useState("woodbine");
  const [race, setRace] = useState("");
  const [loading, setLoading] = useState(false);
  const [leg2Loading, setLeg2Loading] = useState(false);
  const weighting = "equal";
  const [meta, setMeta] = useState(null);
  const [leg2Meta, setLeg2Meta] = useState(null);
  // Refs so async chained handlers always see current state (bypasses stale
  // closure over leftRows / handicapped inside useCallback).
  const leftRowsRef = useRef(leftRows);
  const handicappedRef = useRef(handicapped);
  const trackRef = useRef(track);
  const raceRef = useRef(race);
  useEffect(() => { leftRowsRef.current = leftRows; }, [leftRows]);
  useEffect(() => { handicappedRef.current = handicapped; }, [handicapped]);
  useEffect(() => { trackRef.current = track; }, [track]);
  useEffect(() => { raceRef.current = race; }, [race]);

  // Emit trackContext whenever the dropdown / race input changes so tickets
  // archived manually (without a fresh Load Now) carry the correct track +
  // race. If a prior fetch populated `meta`, we keep its track name / slug /
  // date (richer than the dropdown label) but override race with whatever
  // the user has typed, so a manual race change (e.g. advancing from 6 → 7)
  // is reflected on the next archive.
  useEffect(() => {
    const selected = tracks.find((t) => t.slug === track);
    const todayIso = todayLocalIso();
    onTrackContext?.({
      name: meta?.track || selected?.name || "",
      slug: track,
      race: race || meta?.race || "",
      date: todayIso,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track, race, tracks, meta]);

  // Load the tracks list on mount
  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(`${apiBase}/tracks`);
        if (!resp.ok) return;
        const data = await resp.json();
        if (Array.isArray(data.tracks) && data.tracks.length) {
          setTracks(data.tracks);
          setTrack((prev) =>
            data.tracks.find((t) => t.slug === prev) ? prev : data.tracks[0].slug
          );
        }
      } catch {
        /* ignore — user can still hit the endpoint with the default */
      }
    })();
  }, [apiBase]);

  const fetchOnce = useCallback(
    async ({ silent = false, raceOverride, trackOverride } = {}) => {
      if (loading) return;
      setLoading(true);
      // Read from refs so stale closures (e.g. the one captured by
      // the pendingLoad useEffect at mount time, before setTrack has
      // committed) always see the live user-selected values.
      const effectiveTrack = trackOverride || trackRef.current;
      const effectiveRace =
        raceOverride !== undefined ? raceOverride : raceRef.current;
      const params = new URLSearchParams({ track: effectiveTrack });
      if (effectiveRace) params.set("race", String(effectiveRace));
      const toastId = silent ? null : toast.loading("Fetching live odds…");
      try {
        const resp = await fetch(
          `${apiBase}/fetch-track-odds?${params.toString()}`
        );
        if (!resp.ok) {
          let detail = `HTTP ${resp.status}`;
          try {
            const b = await resp.json();
            if (b?.detail) detail = b.detail;
          } catch {
            /* ignore */
          }
          throw new Error(detail);
        }
        const data = await resp.json();
        setMeta({
          track: data.track,
          race: data.race,
          current_race: data.current_race,
          post_time: data.post_time,
          leg1_winner: data.leg1_winner,
          note: data.note,
          updated_at: data.updated_at,
          cached: data.cached,
        });
        onTrackContext?.({
          name: data.track || "",
          slug: data.track_slug || track,
          race: data.race ?? "",
          date: data.local_date || todayLocalIso(),
        });

        let totalFilled = 0;
        setLeftRows((prev) => {
          let stage = prev;
          if (data.ml && data.ml.length) {
            const { next, filled } = applyEntries(stage, data.ml, "ML");
            stage = next;
            totalFilled += filled;
          }
          if (data.dd && data.dd.length) {
            const { next, filled } = applyEntries(stage, data.dd, "DD");
            stage = clearDD(next, data.dd_missing);
            totalFilled += filled;
          }
          if (data.live_odds && data.live_odds.length) {
            const live = new Map(data.live_odds.map((e) => [Number(e.S), e.odds]));
            stage = stage.map((r) =>
              live.has(Number(r.S)) ? { ...r, LIVE: live.get(Number(r.S)) } : r
            );
          }
          return stage;
        });

        if (totalFilled > 0) {
          onFilled?.();
          onLoaded?.();
        }

        if (toastId) {
          if (totalFilled === 0) {
            toast.warning(
              data.note || "No odds returned for that race yet.",
              { id: toastId, duration: 6000 }
            );
          } else {
            const msg = `Loaded race ${data.race ?? "?"} · ${
              data.ml?.length ?? 0
            } ML · ${data.dd?.length ?? 0} Daily Double prices`;
            if (data.dd?.length) {
              toast.success(msg, { id: toastId, duration: 4500 });
            } else {
              toast.warning(
                `${msg} — Daily Double prices not published yet.`,
                { id: toastId, duration: 5500 }
              );
            }
          }
        }
        return data;
      } catch (err) {
        console.error(err);
        if (toastId) {
          toast.error(String(err.message || err), {
            id: toastId,
            duration: 8000,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [apiBase, loading, setLeftRows, onFilled]
  );

  const fillLeg2 = useCallback(async ({ silent = false, force = false } = {}) => {
    if (leg2Loading) return;
    const toast = silent ? QUIET_TOAST : sonnerToast;
    const currentHandicapped = force || handicappedRef.current;
    const currentLeftRows = leftRowsRef.current;
    if (!currentHandicapped) {
      toast.warning("Handicap race first — Leg 2 fill needs top-3 IQ Power ranks.");
      return;
    }
    const top3 = topDDPORows(currentLeftRows, 3);
    if (top3.length < 1) {
      toast.warning("No valid IQ Power ranks found on the left side.");
      return;
    }
    // Which race is leg-2? Prefer the meta.current_race (what Cybertote is
    // currently on == leg-1), else the race the user typed / the panel loaded.
    const leg1Race = meta?.current_race ?? meta?.race ?? Number(race) ?? null;
    if (!leg1Race) {
      toast.warning("Load track odds first to identify the current race.");
      return;
    }
    const leg2Race = leg1Race + 1;

    setLeg2Loading(true);
    const toastId = toast.loading(
      `Fetching Daily Double probables + race ${leg2Race} ML…`
    );
    try {
      const [probResp, oddsResp] = await Promise.all([
        fetch(`${apiBase}/fetch-dd-probables?track=${track}`),
        fetch(`${apiBase}/fetch-track-odds?track=${track}&race=${leg2Race}`),
      ]);
      if (!probResp.ok) {
        const b = await probResp.json().catch(() => ({}));
        throw new Error(b?.detail || `Probables HTTP ${probResp.status}`);
      }
      if (!oddsResp.ok) {
        const b = await oddsResp.json().catch(() => ({}));
        throw new Error(b?.detail || `Odds HTTP ${oddsResp.status}`);
      }
      const prob = await probResp.json();
      const odds = await oddsResp.json();

      // No probables → still fill leg 2 from ML; SLPO points are 0.
      if (prob.note && (!prob.matrix || Object.keys(prob.matrix).length === 0)) {
        toast.info(`${prob.note} — leg 2 ranked on ML only.`, { duration: 6000 });
      }
      const leg2Posts = prob.leg2_posts || [];
      const sllwp = computeLeg2SLLWP({
        matrix: prob.matrix || {},
        topLeg1: top3,
        leg2Posts,
        weighting,
      });

      // Build right-side rows from union of probables leg-2 posts and Equibase ML posts.
      const mlByS = new Map();
      for (const e of odds.ml || []) {
        const s = Number(e.S);
        if (Number.isInteger(s)) mlByS.set(s, String(e.value ?? ""));
      }
      const allPosts = new Set([
        ...leg2Posts,
        ...Array.from(mlByS.keys()),
      ]);
      const sortedPosts = Array.from(allPosts).sort((a, b) => a - b);

      const newRight = sortedPosts.map((p) => {
        const w = sllwp[p];
        const wp = w?.sllwp;
        return {
          S2: p,
          SLLWP: wp == null ? "" : String(wp),
          SLML: mlByS.get(p) || "",
        };
      });

      // Pad up to 12 minimum for visual consistency with the left half.
      while (newRight.length < 12) {
        newRight.push({ S2: newRight.length + 1, SLLWP: "", SLML: "" });
      }

      setRightRows(newRight);
      onLeg2Filled?.();

      const filledSLLWP = Object.values(sllwp).filter(
        (v) => v.sllwp != null
      ).length;
      setLeg2Meta({
        leg2Race,
        top3: top3.map((h) => h.S),
        note: null,
        samples: filledSLLWP,
        weighting,
      });
      toast.success(
        `Leg-2 (race ${leg2Race}): priced ${filledSLLWP} horses · ${
          odds.ml?.length ?? 0
        } ML`,
        { id: toastId, duration: 4500 }
      );
    } catch (err) {
      console.error(err);
      toast.error(String(err.message || err), {
        id: toastId,
        duration: 8000,
      });
    } finally {
      setLeg2Loading(false);
    }
  }, [
    apiBase,
    track,
    race,
    weighting,
    leg2Loading,
    meta,
    setRightRows,
    onLeg2Filled,
  ]);

  // Latest handlers — oneClick awaits across renders, so call through a ref.
  const latestRef = useRef({});
  latestRef.current = { onHandicap, fillLeg2 };

  // Auto-refresh: re-pull leg-1 odds; if the board was handicapped, also
  // re-price leg 2 and re-rank so the movement arrows update.
  const refreshTick = useCallback(async () => {
    const wasHandicapped = handicappedRef.current;
    const data = await fetchOnce({ silent: true });
    if (!data || !wasHandicapped) return;
    await new Promise((r) => setTimeout(r, 300));
    await latestRef.current.fillLeg2({ silent: true, force: true });
    await new Promise((r) => setTimeout(r, 300));
    latestRef.current.onHandicap?.({ silent: true });
  }, [fetchOnce]);
  // No immediate tick on mount — One-Click owns the first load.
  useAutoRefresh(refreshTick, { enabled: auto, intervalMs: REFRESH_MS, immediate: false });


  const oneClick = useCallback(async () => {
    if (loading || leg2Loading) return;
    // 1) Load leg-1 odds. Wait for the response so we can gate on it.
    const data = await fetchOnce();
    if (!data) return; // fetch already toasted the error
    if (!(data.dd && data.dd.length)) {
      toast.info("No Daily Double will pays yet — IQ Power uses ML + live odds only.", { duration: 6000 });
    }
    // Give React a beat to commit the setLeftRows update.
    await new Promise((r) => setTimeout(r, 300));
    // 2) Handicap left half (quietly — the final step toasts once).
    latestRef.current.onHandicap?.({ silent: true });
    // Wait so leftRowsRef / handicappedRef pick up the new state.
    await new Promise((r) => setTimeout(r, 500));
    // 3) Fill leg 2 (reads refs, so it now sees the fresh state).
    await latestRef.current.fillLeg2();
    // 4) Handicap the right half now that SLLWP/SLML are populated.
    await new Promise((r) => setTimeout(r, 300));
    latestRef.current.onHandicap?.();
  }, [loading, leg2Loading, fetchOnce]);

  // Allow the parent (banner) to imperatively trigger the One-Click
  // Ticket flow for a given track + race. `pendingLoad` changes
  // reference each time the banner is clicked, so we react once per
  // nonce.
  useEffect(() => {
    if (!pendingLoad || !pendingLoad.slug) return;
    setTrack(pendingLoad.slug);
    if (pendingLoad.race != null) setRace(String(pendingLoad.race));
    // Also update the refs synchronously so that the fetchOnce call
    // below (and the chained oneClick pipeline) never reads the pre-
    // click state (e.g. "woodbine" from the dropdown's initial value).
    trackRef.current = pendingLoad.slug;
    if (pendingLoad.race != null) raceRef.current = String(pendingLoad.race);
    const t = setTimeout(() => {
      oneClick();
    }, 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingLoad]);

  // No visible panel: track chips drive loading; auto-refresh is toggled from the DD Matrix.
  return null;
};
